/**
 * Shared instrument desk primitives — fine-tech private banking desk.
 * Trading + AI trading share this shell so operators stay in one unified product.
 *
 * Full Workstation Suite: DeskShell · DeskSessionBar · DeskModule · DeskToolbar · DeskStatusRibbon · DeskChip · TradingLocalNav
 */

'use client'

import React, { type ReactNode, useEffect, useState } from 'react'
import Link from 'next/link'
import { DeskChassis } from './chassis'
import styles from './DeskShell.module.css'

export interface DeskShellProps {
  /** data-surface value for debugging / tests */
  surface?: string
  /** Mono eyebrow above title */
  eyebrow?: string
  title?: string
  lead?: string
  /** Optional chips / ConnectionStatus next to eyebrow */
  headerExtra?: ReactNode
  /** Wider header copy (execution dense desks) */
  headerWide?: boolean
  /** Canonical shared desk-session chrome above the route task. */
  session?: ReactNode
  /** Route-wide spacing density owned by ClientDeskSessionProvider. */
  density?: 'standard' | 'compact'
  /** Keep only the composition contract when a route owns its task header. */
  bare?: boolean
  /** Espresso night / midnight slate / daylight cream — same lock as the flagship desk. */
  deskTheme?: 'espresso' | 'slate' | 'daylight'
  children: ReactNode
  className?: string
}

export function DeskShell({
  surface,
  eyebrow,
  title,
  lead,
  headerExtra,
  headerWide = false,
  session,
  density = 'standard',
  bare = false,
  deskTheme,
  children,
  className = '',
}: DeskShellProps) {
  return (
    <DeskChassis>
    <div
      className={`${styles.bay} ${bare ? styles.bayBare : ''} ${className}`.trim()}
      data-surface={surface}
      data-desk-shell="canonical"
      data-desk-density={density}
      data-desk-theme={deskTheme}
    >
      {session}
      {title ? (
        <header
          className={`${styles.header} ${headerWide ? styles.headerWide : ''} ${!lead ? styles.headerCompact : ''}`.trim()}
        >
          <div className={styles.headerRow}>
            <div className={styles.titleGroup}>
              {eyebrow ? (
                <span className={styles.eyebrow}>
                  <span className={styles.eyebrowDot} aria-hidden />
                  {eyebrow}
                </span>
              ) : null}
              <h1 className={styles.title}>{title}</h1>
              {lead ? <p className={styles.lead}>{lead}</p> : null}
            </div>
            {headerExtra ? <div className={styles.headerExtra}>{headerExtra}</div> : null}
          </div>
        </header>
      ) : null}
      {children}
    </div>
    </DeskChassis>
  )
}

export interface DeskSessionBarProps {
  statusText?: string
  statusTone?: 'live' | 'paper' | 'offline' | 'warning'
  clock?: boolean
  extra?: ReactNode
  children?: ReactNode
  className?: string
}

/**
 * Real-time session telemetry ribbon for active desks.
 */
export function DeskSessionBar({
  statusText = 'Live Desk Stream',
  statusTone = 'live',
  clock = true,
  extra,
  children,
  className = '',
}: DeskSessionBarProps) {
  const [time, setTime] = useState<string>('--:--:--')

  useEffect(() => {
    if (!clock) return
    const updateTime = () => {
      const now = new Date()
      const hh = String(now.getHours()).padStart(2, '0')
      const mm = String(now.getMinutes()).padStart(2, '0')
      const ss = String(now.getSeconds()).padStart(2, '0')
      setTime(`${hh}:${mm}:${ss}`)
    }
    updateTime()
    const timer = setInterval(updateTime, 1000)
    return () => clearInterval(timer)
  }, [clock])

  return (
    <div className={`${styles.sessionBar} ${className}`.trim()} role="status" aria-label="Desk Telemetry">
      <div className={styles.sessionStatus}>
        <span
          className={`${styles.sessionDot} ${
            statusTone === 'live'
              ? styles.dotLive
              : statusTone === 'paper'
                ? styles.dotPaper
                : statusTone === 'warning'
                  ? styles.dotWarning
                  : styles.dotOffline
          }`}
          aria-hidden
        />
        <span className={styles.sessionStatusText}>{statusText}</span>
        {clock ? (
          <>
            <span className={styles.sessionDivider} aria-hidden />
            <span className={styles.sessionClock} aria-label={`Current time ${time}`}>
              {time}
            </span>
          </>
        ) : null}
      </div>
      {children ? <div className={styles.sessionContent}>{children}</div> : null}
      {extra ? <div className={styles.sessionExtra}>{extra}</div> : null}
    </div>
  )
}

export interface DeskModuleProps {
  label: string
  labelId?: string
  count?: ReactNode
  wide?: boolean
  padded?: boolean
  /** Edge-to-edge body (blotter / instrument tables). */
  flush?: boolean
  /** Extra chrome on the bezel (density, filters) — sits left of count. */
  bezelExtra?: ReactNode
  children: ReactNode
  className?: string
  'aria-labelledby'?: string
}

export function DeskModule({
  label,
  labelId,
  count,
  wide = false,
  padded = false,
  flush = false,
  bezelExtra,
  children,
  className = '',
  'aria-labelledby': ariaLabelledBy,
}: DeskModuleProps) {
  const id = labelId ?? `desk-module-${label.toLowerCase().replace(/\s+/g, '-')}`
  return (
    <section
      className={`${styles.module} ${wide ? styles.moduleWide : ''} ${className}`.trim()}
      aria-labelledby={ariaLabelledBy ?? id}
    >
      <div className={styles.moduleBezel}>
        <div className={styles.moduleBezelLeft}>
          <span className={styles.moduleMark} aria-hidden />
          <span className={styles.moduleLabel} id={id}>
            {label}
          </span>
          {count != null ? (
            <span className={styles.moduleCount} aria-live="polite">
              {count}
            </span>
          ) : null}
        </div>
        {bezelExtra ? <div className={styles.moduleExtra}>{bezelExtra}</div> : null}
      </div>
      <div
        className={`${styles.moduleBody} ${padded ? styles.moduleBodyPadded : ''} ${flush ? styles.moduleBodyFlush : ''}`.trim()}
      >
        {children}
      </div>
    </section>
  )
}

export interface DeskToolbarProps {
  left?: ReactNode
  right?: ReactNode
  children?: ReactNode
  className?: string
}

/**
 * Standardized horizontal tool rail with left/right slots.
 */
export function DeskToolbar({ left, right, children, className = '' }: DeskToolbarProps) {
  return (
    <div className={`${styles.toolbar} ${className}`.trim()} role="toolbar">
      {left ? <div className={styles.toolbarLeft}>{left}</div> : null}
      {children}
      {right ? <div className={styles.toolbarRight}>{right}</div> : null}
    </div>
  )
}

export interface DeskStatusRibbonProps {
  left: ReactNode
  right?: ReactNode
  className?: string
}

/**
 * Bottom status ribbon for tables and blotters.
 */
export function DeskStatusRibbon({ left, right, className = '' }: DeskStatusRibbonProps) {
  return (
    <div className={`${styles.statusRibbon} ${className}`.trim()}>
      <div className={styles.statusRibbonLeft}>{left}</div>
      {right ? <div className={styles.statusRibbonRight}>{right}</div> : null}
    </div>
  )
}

export type TradingLocalSegment = 'orders' | 'execution'

export interface TradingLocalNavProps {
  active: TradingLocalSegment
  className?: string
}

const TRADING_LOCAL: { id: TradingLocalSegment; href: string; label: string }[] = [
  { id: 'orders', href: '/trading', label: 'Orders' },
  { id: 'execution', href: '/trading/execution', label: 'Execution' },
]

/**
 * Trading-local strip (Orders · Execution). Agents / Markets live in the sidebar.
 */
export function TradingLocalNav({ active, className = '' }: TradingLocalNavProps) {
  return (
    <nav
      className={`${styles.localNav} ${className}`.trim()}
      aria-label="Trading workflows"
    >
      {TRADING_LOCAL.map((item) => {
        const isActive = item.id === active
        return (
          <Link
            key={item.id}
            href={item.href}
            className={`${styles.localLink} ${isActive ? styles.localLinkActive : ''}`.trim()}
            aria-current={isActive ? 'page' : undefined}
          >
            {item.label}
          </Link>
        )
      })}
    </nav>
  )
}

export { DeskChip } from './chassis'
export { styles as deskShellStyles }
